"use client";
import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Typography } from "@/components/ui";
import { env } from "@/lib/environment";

interface AppWidget {
  id: number;
  name: string;
  bgUrl: string;
  iconUrl: string;
}

interface AppWidgetsSectionProps {
  appWidgetsData: AppWidget[];
  imgBaseUrl: string;
  title?: string;
  subtitle?: React.ReactNode;
  ctaHref?: string;
  ctaLabel?: string;
}

export function AppWidgetsSection({ appWidgetsData, imgBaseUrl, title = "Impressive Apps & Widgets to Keep Your Digital Signage Up-to-date", subtitle = "Get real-time, relevant, informative and beautiful contents with Pickcel digital signage Apps.", ctaHref = "/digital-signage-apps", ctaLabel = "View All →" }: AppWidgetsSectionProps) {
  const [appIndex, setAppIndex] = useState(0);

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center">
      <div className="flex justify-center order-2 lg:order-1 relative">
        <div className="absolute top-10 left-4 w-full max-w-md">
          <Image
            src={`${env.NEXT_PUBLIC_CDN_URL}/images/shared/screen-befrore.FK5t4M77SzVC.svg`}
            alt="Digital Signage Display"
            title="Digital Signage Display"
            width={400}
            height={500}
            className="w-full h-auto"
          />
        </div>
        <div className="w-full max-w-md -translate-2">
          <Image
            src={imgBaseUrl + appWidgetsData[appIndex].bgUrl}
            alt="Digital Signage Display with CNN News"
            title="Digital Signage Display with CNN News"
            width={400}
            height={500}
            className="w-full h-auto border-3 border-black rounded-sm"
          />
        </div>
      </div>
      <div className="space-y-8 order-1 lg:order-2 text-right">
        <Typography variant="h2" as="h2">
          {title}
        </Typography>
        <Typography variant="large" as="p">
          {subtitle}
        </Typography>

        <div className="flex justify-end items-end space-x-4 w-full">
          <div className="flex space-x-3">
            {appWidgetsData.map(app => (
              <div
                key={app.id}
                className={`md:w-20 md:h-20 w-14 h-14 cursor-pointer  flex items-center justify-center rounded-3xl hover:shadow-2xl ${
                  appIndex === app.id ? "shadow-2xl" : ""
                }`}
                onClick={() => setAppIndex(app.id)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setAppIndex(app.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Select ${app.name} app`}
              >
                <Image
                  src={imgBaseUrl + app.iconUrl}
                  alt={app.name}
                  title={app.name}
                  width={80}
                  height={80}
                  className="w-20 h-20"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="text-right">
          <Link
            href="/digital-signage-apps"
            className="border border-primary text-primary rounded-lg px-4 py-2"
          >
            View All →
          </Link>
        </div>
      </div>
    </div>
  );
}
